///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import {
    EngineAntennaDTO,
    EngineProductDTO,
    PdbChannelFrequenciesEnum,
    ProductTypeEnum
} from '../../../../../generated/shared-types.model';
import { LinkFeature } from '../../../entities/link/link-feature';
import { isDefined } from '@siklu/sui-lib/utils';
import { ProjectProductsService } from '../../../engine/project-products-service';

export class LinkProductsPairModel {
    public readonly title: string;
    public extendedByT280DnLink: boolean;
    private readonly supportedTuAntennas: EngineAntennaDTO[] = [];

    protected constructor(
        public readonly leadingLocationName: string,
        public readonly compliantLocation: string,
        public readonly leadingProduct: EngineProductDTO,
        public readonly leadingAntenna: EngineAntennaDTO,
        public readonly compliantProduct: EngineProductDTO,
        public readonly compliantAntenna: EngineAntennaDTO
    ) {
        this.title =
            this.leadingProduct.name === this.compliantProduct.name
                ? this.leadingProduct.name
                : `${this.leadingProduct.name} -> ${this.compliantProduct.name}`;

        if (compliantProduct.isTU) {
            this.supportedTuAntennas.push(compliantAntenna);
        }
    }

    static createForLink(link: LinkFeature, productA: EngineProductDTO, productB: EngineProductDTO): LinkProductsPairModel {
        const antennaA = productA.pdbConfig.antennas.find((antenna) => antenna.id === link.getUsedAnt0());
        const antennaB = productB.pdbConfig.antennas.find((antenna) => antenna.id === link.getUsedAnt1());

        if (productA.isPtP || productA.isBU) {
            return new LinkProductsPairModel(
                link.getFromStation().getName(),
                link.getToStation().getName(),
                productA,
                antennaA,
                productB,
                antennaB
            );
        } else {
            return new LinkProductsPairModel(
                link.getToStation().getName(),
                link.getFromStation().getName(),
                productB,
                antennaB,
                productA,
                antennaA
            );
        }
    }

    checkAndCreateTransitionPair(
        newProductA: EngineProductDTO,
        antennaAId: number,
        newProductB: EngineProductDTO,
        antennaBId: number
    ): LinkProductsPairModel | undefined {
        if (!isDefined(newProductB)) {
            newProductB = newProductA;
            antennaBId = antennaAId;
        }

        const antennaA = newProductA.pdbConfig.antennas.find((antenna) => antenna.id === antennaAId);
        const antennaB = newProductB.pdbConfig.antennas.find((antenna) => antenna.id === antennaBId);

        let newPair: LinkProductsPairModel;

        if (newProductA.isPtP || newProductA.isBU) {
            newPair = new LinkProductsPairModel(
                this.leadingLocationName,
                this.compliantLocation,
                newProductA,
                antennaA,
                newProductB,
                antennaB
            );
        } else {
            newPair = new LinkProductsPairModel(
                this.leadingLocationName,
                this.compliantLocation,
                newProductB,
                antennaB,
                newProductA,
                antennaA
            );
        }

        return this.isProductsTransitionPossible(newPair) ? newPair : undefined;
    }

    getProductByLocationName(name: string): EngineProductDTO {
        return name === this.leadingLocationName ? this.leadingProduct : this.compliantProduct;
    }

    getSupportedFrequencies(): PdbChannelFrequenciesEnum[] {
        const frequenciesSet = new Set<PdbChannelFrequenciesEnum>();

        this.leadingProduct.pdbConfig.channelConfigs
            .flatMap((channelConfig) => channelConfig.frequencies)
            .forEach((frequency) => frequenciesSet.add(frequency));

        return Array.from(frequenciesSet.values()).sort((a, b) => a - b);
    }

    getDefaultAntennaByLocationName(name: string): EngineAntennaDTO {
        return name === this.leadingLocationName ? this.leadingAntenna : this.compliantAntenna;
    }

    isPtP(): boolean {
        return this.leadingProduct.isPtP;
    }

    isDN() {
        return this.leadingProduct.isBU && this.compliantProduct.isBU;
    }

    isCN() {
        return this.leadingProduct.isBU && this.compliantProduct.isTU;
    }

    productsEqual(productPair: LinkProductsPairModel): boolean {
        return this.leadingProduct.id === productPair.leadingProduct.id && this.compliantProduct.id === productPair.compliantProduct.id;
    }

    isSupportsPolarity(): boolean {
        return this.leadingProduct.productType === ProductTypeEnum.TG_MH && (this.isDN() || this.isCN());
    }

    addSupportedTuAntenna(compliantAntenna: EngineAntennaDTO): void {
        if (!this.supportedTuAntennas.some((antenna) => antenna.id === compliantAntenna.id)) {
            this.supportedTuAntennas.push(compliantAntenna);
        }
    }

    getAvailableAntennasForLocation(locationName: string): EngineAntennaDTO[] {
        const product = this.getProductByLocationName(locationName);
        return product.isTU ? this.supportedTuAntennas : product.pdbConfig.antennas;
    }

    private isProductsTransitionPossible(productsPair: LinkProductsPairModel): boolean {
        if (productsPair.isPtP()) {
            return true;
        }

        if (productsPair.leadingProduct.productType === ProductTypeEnum.MH) {
            return productsPair.leadingProduct.id === productsPair.compliantProduct.id;
        }

        const isDnWithSameSectorCount =
            this.isDN() &&
            productsPair.isDN() &&
            productsPair.leadingProduct.id === productsPair.compliantProduct.id &&
            this.leadingProduct.pdbConfig.sectorConfig.length === productsPair.leadingProduct.pdbConfig.sectorConfig.length;

        const isCnWithSameBuSectors =
            this.isCN() &&
            productsPair.isCN() &&
            this.leadingProduct.pdbConfig.sectorConfig.length === productsPair.leadingProduct.pdbConfig.sectorConfig.length;

        const isT280Cn =
            this.isDN() &&
            this.leadingProduct.id === productsPair.leadingProduct.id &&
            productsPair.compliantProduct.id === ProjectProductsService.MH_T280_TU_ID;
        productsPair.extendedByT280DnLink = isT280Cn;

        return isDnWithSameSectorCount || isCnWithSameBuSectors || isT280Cn;
    }
}
